import { useState } from 'react'
import { useRoomViewStore } from 'store/roomView/roomView.store'
import {
  EPatientStatus,
  TRoomViewScheduledSurgery,
  TRoomViewSurgeryStaff,
} from '../RoomView.types'
import { useUpdatePatientStatusMutation } from '../hooks/surgeries.hook'
import { enqueueSnackbar } from 'notistack'
import { useAddRemoveSurgeryStaff } from '../hooks/surgeryStaff.hook'
import { useNavigate } from 'react-router-dom'

export const useRoomGridLogic = () => {
  const {
    rooms,
    surgeryStaff,
    roomViewSettings,
    scheduledSurgeries,
    patientStatusOptions,
    availableSurgeryStaff,
    isLoading: isLoadingRoomView,
    refetchAll,
  } = useRoomViewStore()

  const navigate = useNavigate()

  const [updatePatientStatus] = useUpdatePatientStatusMutation()
  const [addRemoveSurgeryStaff, { loading: isLoadingAddRemoveSurgeryStaff }] =
    useAddRemoveSurgeryStaff()

  const [editStaffDialogOpen, setEditStaffDialogOpen] = useState(false)
  const [editingRoomIndex, setEditingRoomIndex] = useState<number | null>(null)
  const [statusAnchorEl, setStatusAnchorEl] = useState<HTMLElement | null>(null)
  const [selectedSurgeryInfo, setSelectedSurgeryInfo] = useState<{
    surgeryId: string
    initialStatus: EPatientStatus
  } | null>(null)
  const [selectedStaffForEdit, setSelectedStaffForEdit] = useState<{
    initialStaff: TRoomViewSurgeryStaff[]
    newStaff: TRoomViewSurgeryStaff[]
  }>({ initialStaff: [], newStaff: [] })
  const [addPatientAnchorEl, setAddPatientAnchorEl] =
    useState<HTMLElement | null>(null)
  const [selectedRoomIndex, setSelectedRoomIndex] = useState<number | null>(
    null
  )
  const [staffPopoverAnchor, setStaffPopoverAnchor] =
    useState<HTMLElement | null>(null)

  const selectedRoomId =
    selectedRoomIndex !== null ? rooms[selectedRoomIndex]?.id : undefined
  const isLoading = isLoadingRoomView || isLoadingAddRemoveSurgeryStaff
  const selectedSurgeryStaff =
    selectedRoomIndex !== null
      ? scheduledSurgeries.find(
          (surgery) => surgery._id === rooms[selectedRoomIndex]?.procedureId
        )?.surgeryStaff
      : []

  const handleAddRoomsClick = () => {
    navigate('/location-management')
  }

  const getNextStatus = (
    currentStatus: EPatientStatus
  ): EPatientStatus | null => {
    const statusOrder = [
      EPatientStatus.SCHEDULED,
      EPatientStatus.CHECKED_IN,
      EPatientStatus.ASSIGN_ROOM,
      EPatientStatus.CHECKED_OUT,
    ]

    const currentIndex = statusOrder.indexOf(currentStatus)
    const nextIndex = currentIndex + 1

    return nextIndex < statusOrder.length ? statusOrder[nextIndex] : null
  }

  const shouldHighlightStatus = (status: EPatientStatus): boolean => {
    if (!selectedSurgeryInfo) return false
    const nextStatus = getNextStatus(selectedSurgeryInfo.initialStatus)
    return status === nextStatus
  }

  const handleStatusClick = (
    event: React.MouseEvent<HTMLElement>,
    surgeryId: string
  ) => {
    const initialStatus = scheduledSurgeries.find(
      (surgery) => surgery._id === surgeryId
    )?.patientStatusHistory[0]?.status

    setStatusAnchorEl(event.currentTarget)

    setSelectedSurgeryInfo({
      surgeryId,
      initialStatus: initialStatus || EPatientStatus.CHECKED_IN,
    })
  }

  const handleStatusChange = (newStatus: EPatientStatus) => async () => {
    if (selectedSurgeryInfo) {
      const { surgeryId, initialStatus } = selectedSurgeryInfo

      if (newStatus === initialStatus) return
      try {
        await updatePatientStatus({
          variables: {
            procedureId: surgeryId,
            patientStatus: newStatus,
          },
        })
        enqueueSnackbar('Patient status updated successfully', {
          variant: 'success',
        })
        refetchAll()
        handleStatusClose()
      } catch (error) {
        enqueueSnackbar('Error updating patient status', {
          variant: 'error',
        })
      }
    }
    handleStatusClose()
  }

  const handleStatusClose = () => {
    setStatusAnchorEl(null)
    setSelectedSurgeryInfo(null)
  }

  const handleEditStaff =
    (surgery: TRoomViewScheduledSurgery, roomIndex: number) => () => {
      setEditingRoomIndex(roomIndex)
      setSelectedStaffForEdit({
        initialStaff: surgery.surgeryStaff,
        newStaff: surgery.surgeryStaff,
      })
      setEditStaffDialogOpen(true)
    }

  const handleStaffSelectionChange = (newValue: TRoomViewSurgeryStaff[]) => {
    setSelectedStaffForEdit((prev) => ({
      ...prev,
      newStaff: newValue,
    }))
  }

  const handleSaveStaff = async () => {
    if (editingRoomIndex === null) return

    const surgery = scheduledSurgeries.find(
      (surgery) => surgery._id === rooms[editingRoomIndex]?.procedureId
    )

    if (!surgery) return

    const initialStaffIds = selectedStaffForEdit.initialStaff
      .map((staff) => staff._id)
      .sort()
    const newStaffIds = selectedStaffForEdit.newStaff
      .map((staff) => staff._id)
      .sort()

    const haveSameStaff =
      initialStaffIds.length === newStaffIds.length &&
      initialStaffIds.every((id, index) => id === newStaffIds[index])

    if (haveSameStaff) {
      enqueueSnackbar('No changes made to staff', {
        variant: 'info',
      })
      handleCancelStaffEdit()
      return
    }

    try {
      await addRemoveSurgeryStaff({
        variables: {
          input: {
            surgeryId: surgery._id,
            surgeryStaffIds: newStaffIds,
          },
        },
      })

      await refetchAll()
      handleCancelStaffEdit()
      enqueueSnackbar('Surgery staff saved successfully', {
        variant: 'success',
      })
    } catch (error) {
      enqueueSnackbar('Error saving surgery staff', {
        variant: 'error',
      })
    }
  }

  const handleCancelStaffEdit = () => {
    setEditingRoomIndex(null)
    setEditStaffDialogOpen(false)
    setSelectedStaffForEdit({
      initialStaff: [],
      newStaff: [],
    })
  }

  const handleAddPatientClick = (
    event: React.MouseEvent<HTMLElement>,
    roomIndex: number
  ) => {
    setAddPatientAnchorEl(event.currentTarget)
    setSelectedRoomIndex(roomIndex)
  }

  const handleStaffClick = (
    event: React.MouseEvent<HTMLElement>,
    roomIndex: number
  ) => {
    setStaffPopoverAnchor(event.currentTarget)
    setSelectedRoomIndex(roomIndex)
  }

  const handleStaffPopoverClose = () => {
    setStaffPopoverAnchor(null)
    setSelectedRoomIndex(null)
  }

  const handleAddPatientClose = () => {
    setAddPatientAnchorEl(null)
  }

  // const handleRoomNameClick = (index: number) => {
  //   setScheduleRoomIndex(index)
  //   setScheduleModalOpen(true)
  // }

  return {
    rooms,
    isLoading,
    surgeryStaff,
    statusAnchorEl,
    selectedRoomId,
    roomViewSettings,
    selectedRoomIndex,
    staffPopoverAnchor,
    scheduledSurgeries,
    addPatientAnchorEl,
    selectedSurgeryInfo,
    editStaffDialogOpen,
    selectedStaffForEdit,
    selectedSurgeryStaff,
    patientStatusOptions,
    availableSurgeryStaff,
    handleSaveStaff,
    handleEditStaff,
    handleStaffClick,
    handleStatusClick,
    handleStatusClose,
    handleStatusChange,
    handleAddRoomsClick,
    handleCancelStaffEdit,
    handleAddPatientClick,
    handleAddPatientClose,
    setEditStaffDialogOpen,
    handleStaffPopoverClose,
    setSelectedStaffForEdit,
    handleStaffSelectionChange,
    shouldHighlightStatus,
  }
}
